import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Edit3, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";

const KIND_META = {
    events: { title: "Eventi", singular: "Evento", publicBase: "/eventi" },
    productions: { title: "Produzioni TV", singular: "Produzione", publicBase: "/produzioni-tv" },
    formats: { title: "Format TV", singular: "Format", publicBase: "/format-tv" },
    talents: { title: "Talent & Ospiti", singular: "Talent", publicBase: "/talent-ospiti" },
};

export default function AdminList() {
    const { kind } = useParams();
    const meta = KIND_META[kind];
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/${kind}`);
            setItems(r.data);
        } catch {
            toast.error("Errore caricamento");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [kind]);

    const remove = async (id) => {
        if (!window.confirm("Eliminare definitivamente questo contenuto?")) return;
        try {
            await api.delete(`/admin/${kind}/${id}`);
            toast.success("Eliminato");
            load();
        } catch {
            toast.error("Errore eliminazione");
        }
    };

    const toggleStatus = async (item) => {
        try {
            const newStatus = item.status === "published" ? "draft" : "published";
            await api.put(`/admin/${kind}/${item.id}`, { ...item, status: newStatus });
            toast.success(newStatus === "published" ? "Pubblicato" : "Salvato in bozza");
            load();
        } catch {
            toast.error("Errore");
        }
    };

    if (!meta) return <div>Sezione non valida</div>;

    return (
        <div data-testid={`admin-list-${kind}`}>
            <Toaster position="top-center" richColors />
            <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
                <div>
                    <div className="kicker mb-3">CMS · {meta.title}</div>
                    <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-[-0.03em]">
                        {meta.title}
                    </h1>
                </div>
                <Link
                    to={`/admin/${kind}/new`}
                    className="btn-primary"
                    data-testid={`admin-new-${kind}`}
                >
                    <Plus size={16} /> Nuovo {meta.singular}
                </Link>
            </div>

            {loading ? (
                <div className="text-white/50">Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="border border-white/10 p-12 text-center" data-testid="admin-list-empty">
                    <p className="text-white/60 mb-5">Nessun contenuto presente.</p>
                    <Link to={`/admin/${kind}/new`} className="btn-primary">
                        <Plus size={16} /> Crea il primo
                    </Link>
                </div>
            ) : (
                <div className="border border-white/10 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-table">
                        <thead className="bg-white/5 text-white/60">
                            <tr>
                                <th className="text-left px-4 py-3 font-normal kicker">Cover</th>
                                <th className="text-left px-4 py-3 font-normal kicker">Titolo</th>
                                <th className="text-left px-4 py-3 font-normal kicker hidden md:table-cell">Categoria</th>
                                <th className="text-left px-4 py-3 font-normal kicker hidden md:table-cell">Anno</th>
                                <th className="text-left px-4 py-3 font-normal kicker">Stato</th>
                                <th className="text-right px-4 py-3 font-normal kicker">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.id} className="border-t border-white/10 hover:bg-white/[0.02]" data-testid={`admin-row-${it.slug}`}>
                                    <td className="px-4 py-3">
                                        <div className="w-14 h-14 bg-neutral-800 overflow-hidden">
                                            {it.cover_image && (
                                                <img src={mediaUrl(it.cover_image)} alt="" className="w-full h-full object-cover" />
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold text-white">{it.title}</div>
                                        <div className="text-white/40 text-xs">/{it.slug}</div>
                                    </td>
                                    <td className="px-4 py-3 text-white/70 hidden md:table-cell">{it.category || "—"}</td>
                                    <td className="px-4 py-3 text-white/70 hidden md:table-cell">{it.year || "—"}</td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => toggleStatus(it)}
                                            data-testid={`toggle-status-${it.slug}`}
                                            className={`inline-flex items-center gap-2 px-3 py-1 text-xs uppercase tracking-[0.16em] border ${
                                                it.status === "published"
                                                    ? "border-[color:var(--brand-blue)] text-[color:var(--brand-blue)]"
                                                    : "border-white/20 text-white/60"
                                            }`}
                                        >
                                            {it.status === "published" ? <Eye size={12} /> : <EyeOff size={12} />}
                                            {it.status === "published" ? "Pubblicato" : "Bozza"}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-2">
                                            {it.status === "published" && (
                                                <a
                                                    href={`${meta.publicBase}/${it.slug}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="p-2 hover:text-[color:var(--brand-blue)]"
                                                    title="Vedi pagina pubblica"
                                                    data-testid={`view-public-${it.slug}`}
                                                >
                                                    <ExternalLink size={16} />
                                                </a>
                                            )}
                                            <Link
                                                to={`/admin/${kind}/${it.id}`}
                                                className="p-2 hover:text-[color:var(--brand-blue)]"
                                                title="Modifica"
                                                data-testid={`edit-${it.slug}`}
                                            >
                                                <Edit3 size={16} />
                                            </Link>
                                            <button
                                                onClick={() => remove(it.id)}
                                                className="p-2 hover:text-red-400"
                                                title="Elimina"
                                                data-testid={`delete-${it.slug}`}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
